import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import * as warehouseApi from "@/lib/api/warehouse";
import { useAuth } from "@/context/AuthContext";
import { canWorkSales, canOperateInventory } from "@/lib/permissions";
import { PICKABLE_STATUSES } from "@/lib/sales-orders";
import { StatusBadge } from "@/components/base/StatusBadge";
import { TableSkeleton } from "@/components/base/TableSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { variantLabel } from "@/lib/format";
import { firstRelation } from "@/lib/relations";
import {
  pickTaskBadgeStatus,
  pickTaskLabel,
  shipmentAppearsInShippingQueue,
  shipmentCanDispatchFromQueue,
  shipmentPackageCoverage,
} from "@/lib/warehouse-workflow";
import {
  canonicalLineSku,
  pickLocationHeader,
  pickSlicesFromReservations,
  shipmentCarrierLabel,
  shipmentLifecycleComplete,
  shipmentOperatorStatus,
  type AllocationLedgerEntry,
  type PickSlice,
} from "@/lib/warehouse-projection";

import { printSalesOrderDocuments } from "@/lib/api/order-documents";
import { canPrintOrderDocuments } from "@/lib/order-documents";
import { PrintDocumentsMenu } from "@/pages/sales-orders/components/PrintDocumentsMenu";
import { BulkActionBar } from "@/components/feature/BulkActionBar";
import { useBulkSelection } from "@/components/feature/useBulkSelection";
import {
  fulfilmentLookupPath,
  fulfilmentPackingPath,
  fulfilmentPickPath,
  fulfilmentShipmentPath,
  formatShipTo,
  parseFulfilmentTab,
  visibleFulfilmentPrintOrderIds,
  type FulfilmentTab,
} from "@/lib/fulfilment-workspace";
import { australiaPostTrackingUrl, isAustraliaPostShipment, viewPrintLabelAction } from "@/lib/shipment-timeline";
import Shipments from "@/pages/shipments/page";

interface PickOrder {
  id: string;
  order_number: string;
  status: string;
  location_id: string | null;
  priority: string | null;
  shipping_address?: Record<string, unknown> | null;
  customers?: { name: string } | null;
  locations?: { name: string } | null;
}

interface PickLine extends CatalogueHistoryFields {
  id: string;
  sales_order_id: string;
  product_id: string | null;
  variant_id: string | null;
  sku?: string | null;
  qty_ordered: number;
  qty_allocated: number;
  qty_fulfilled: number;
  products?: { name: string; sku: string | null } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface PickTaskRow {
  id: string;
  sales_order_id: string;
  location_id: string | null;
  status: string;
  assigned_to: string | null;
  locations?: { name: string } | null;
}

interface PickItemRow extends CatalogueHistoryFields {
  id: string;
  pick_task_id: string;
  sales_order_item_id: string | null;
  product_id: string | null;
  variant_id: string | null;
  qty_required: number;
  qty_picked: number;
  products?: { name: string; sku: string | null } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface TaskCard {
  task: PickTaskRow;
  order: PickOrder;
  items: (PickItemRow & { remaining: number })[];
}

interface UnstartedCard {
  order: PickOrder;
  slices: PickSlice[];
}

interface FulfilmentRow {
  id: string;
  fulfilment_number: string;
  sales_order_id: string | null;
  location_id: string | null;
  status: string;
  locations?: { name: string } | null;
  sales_orders?: { id?: string; order_number: string; shipping_address?: Record<string, unknown> | null; customers?: { name: string } | null } | null;
}

interface FulfilmentItemRow extends CatalogueHistoryFields {
  id: string;
  fulfilment_id: string;
  product_id: string | null;
  qty_fulfilled: number;
  products?: { name: string; sku: string | null; weight_kg: number | null; length_cm: number | null; width_cm: number | null; height_cm: number | null } | null;
}

interface ShipmentRow {
  id: string;
  fulfilment_id: string;
  status: string;
  tracking_number: string | null;
  shipped_at: string | null;
  shipping_cost: number | null;
  shipping_provider: string | null;
  service_name: string | null;
  provider_sync_status: string | null;
  provider_shipment_id: string | null;
  carrier_reference: string | null;
  label_url: string | null;
  carrier_services?: { name: string; carrier_code?: string | null } | null;
  fulfilments?: { fulfilment_number: string; sales_orders?: { id?: string; order_number: string; shipping_address?: Record<string, unknown> | null; customers?: { name: string } | null } | null } | null;
}

interface PkgRow {
  shipment_id: string;
  items: { fulfilment_item_id?: string; product_name?: string; qty?: number }[] | null;
}

export default function Warehouse() {
  const { org, role, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = parseFulfilmentTab(params.get("tab"));
  const setTab = (next: FulfilmentTab) => setParams({ tab: next }, { replace: true });
  const [pickFilter, setPickFilter] = useState<"all" | "mine" | "unassigned">("all");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const canShip = canWorkSales(role);
  const canPick = canOperateInventory(role);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["warehouse", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const pickOrders = await supabase
        .from("sales_orders")
        .select("id, order_number, status, location_id, priority, shipping_address, customers(name), locations(name)")
        .eq("org_id", org!.id)
        .in("status", PICKABLE_STATUSES)
        .order("created_at", { ascending: true });
      const pickOrderIds = (pickOrders.data ?? []).map((o) => o.id);

      const [pickLines, pickTasks, fulfilments] = await Promise.all([
        pickOrderIds.length
          ? supabase
              .from("sales_order_items")
              .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name, sku, variant_attributes, id, sales_order_id, product_id, variant_id, qty_ordered, qty_allocated, qty_fulfilled, products(name, sku), variants(sku, option_values)")
              .eq("org_id", org!.id)
              .in("sales_order_id", pickOrderIds)
              .order("created_at")
          : Promise.resolve({ data: [] }),
        supabase
          .from("pick_tasks")
          .select("id, sales_order_id, location_id, status, assigned_to, locations(name)")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(300),
        supabase
          .from("fulfilments")
          .select("id, fulfilment_number, sales_order_id, location_id, status, locations(name), sales_orders(id, order_number, shipping_address, customers(name))")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(200),
      ]);

      // Scope pick items to the open tasks actually shown (avoids loading every
      // pick item in the org).
      const openTaskIds = (pickTasks.data ?? [])
        .filter((t) => ["pending", "in_progress"].includes(t.status))
        .map((t) => t.id);
      const pickItems = openTaskIds.length
        ? await supabase
            .from("pick_items")
            .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, pick_task_id, sales_order_item_id, product_id, variant_id, qty_required, qty_picked, products(name, sku), variants(sku, option_values)")
            .in("pick_task_id", openTaskIds)
        : { data: [] };

      const fulfilmentIds = (fulfilments.data ?? []).map((f) => f.id);
      const [fulfilmentItems, shipments] = await Promise.all([
        fulfilmentIds.length
          ? supabase
              .from("fulfilment_items")
              .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, fulfilment_id, product_id, qty_fulfilled, products(name, sku, weight_kg, length_cm, width_cm, height_cm)")
              .eq("org_id", org!.id)
              .in("fulfilment_id", fulfilmentIds)
              .order("created_at")
          : Promise.resolve({ data: [] }),
        supabase
          .from("shipments")
          .select("id, fulfilment_id, status, tracking_number, shipped_at, shipping_cost, shipping_provider, service_name, provider_sync_status, provider_shipment_id, carrier_reference, label_url, carrier_services(name, carrier_code), fulfilments(fulfilment_number, sales_orders(id, order_number, shipping_address, customers(name)))")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(300),
      ]);

      // Scope packages to the loaded shipments (avoids loading every package).
      const shipmentIds = (shipments.data ?? []).map((s) => s.id);
      const packages = shipmentIds.length
        ? await supabase.from("shipment_packages").select("shipment_id, items").in("shipment_id", shipmentIds)
        : { data: [] };

      const ledgerRefs = [...pickOrderIds, ...(pickLines.data ?? []).map((l) => l.id)];
      const ledgerRes = ledgerRefs.length
        ? await supabase
            .from("inventory_ledger")
            .select("entry_type, qty_change, location_id, reference_type, reference_id, variant_id, locations(name)")
            .eq("org_id", org!.id)
            .in("entry_type", ["allocation", "deallocation"])
            .in("reference_id", ledgerRefs)
        : { data: [] };

      return {
        pickOrders: (pickOrders.data ?? []).map((row) => ({
          ...row,
          customers: firstRelation(row.customers),
          locations: firstRelation(row.locations),
        })) as PickOrder[],
        pickLines: (pickLines.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as PickLine[],
        allocationLedger: ((ledgerRes.data ?? []) as Array<{
          entry_type: string;
          qty_change: number;
          location_id: string | null;
          reference_type: string | null;
          reference_id: string | null;
          variant_id: string | null;
          locations: { name: string } | { name: string }[] | null;
        }>).map((row) => ({
          entry_type: row.entry_type,
          qty_change: row.qty_change,
          location_id: row.location_id,
          reference_type: row.reference_type,
          reference_id: row.reference_id,
          variant_id: row.variant_id,
          location_name: firstRelation(row.locations)?.name ?? null,
        })) as AllocationLedgerEntry[],
        pickTasks: (pickTasks.data ?? []).map((row) => ({
          ...row,
          locations: firstRelation(row.locations),
        })) as PickTaskRow[],
        pickItems: (pickItems.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as PickItemRow[],
        fulfilments: (fulfilments.data ?? []).map((row) => {
          const salesOrder = firstRelation(row.sales_orders);
          return {
            ...row,
            locations: firstRelation(row.locations),
            sales_orders: salesOrder
              ? {
                  ...salesOrder,
                  customers: firstRelation(salesOrder.customers),
                }
              : null,
          };
        }) as FulfilmentRow[],
        fulfilmentItems: (fulfilmentItems.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
        })) as FulfilmentItemRow[],
        shipments: (shipments.data ?? []).map((row) => {
          const fulfilment = firstRelation(row.fulfilments);
          const salesOrder = fulfilment
            ? firstRelation(fulfilment.sales_orders)
            : null;
          return {
            ...row,
            carrier_services: firstRelation(row.carrier_services),
            fulfilments: fulfilment
              ? {
                  ...fulfilment,
                  sales_orders: salesOrder
                    ? {
                        ...salesOrder,
                        customers: firstRelation(salesOrder.customers),
                      }
                    : null,
                }
              : null,
          };
        }) as ShipmentRow[],
        packages: (packages.data ?? []) as PkgRow[],
      };
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["warehouse", org?.id] });

  const dispatch = async (shipmentId: string) => {
    setBusyId(shipmentId);
    setError("");
    try {
      await warehouseApi.updateShipmentStatus(shipmentId, "shipped");
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
    setBusyId(null);
  };

  const startPick = async (soId: string) => {
    setBusyId(soId);
    setError("");
    let taskId: string;
    try {
      taskId = await warehouseApi.createPickTask(soId);
    } catch (e) {
      setBusyId(null);
      setError((e as Error).message);
      return;
    }
    setBusyId(null);
    navigate(fulfilmentPickPath(taskId));
  };

  const createShipment = async (fulfilmentId: string) => {
    setBusyId(fulfilmentId);
    setError("");
    let createdId: string;
    try {
      createdId = await warehouseApi.createShipmentHeader(fulfilmentId);
    } catch (e) {
      setBusyId(null);
      setError((e as Error).message);
      return;
    }
    setBusyId(null);
    refresh();
    navigate(fulfilmentShipmentPath(createdId));
  };

  const printOrder = async (orderId: string, kind: "pick" | "pack") => {
    if (!org?.id) return;
    setError("");
    try {
      await printSalesOrderDocuments(org.id, [orderId], kind);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const term = search.trim().toLowerCase();

  // Per-location pick tasks are the unit of work: a split order (e.g. 6 in
  // Warehouse A + 4 in Warehouse B) produces one task per physical location.
  // Group open tasks by location, never by order, so "Pick 6 — A" and
  // "Pick 4 — B" are never merged into an ambiguous combined task.
  const picking = useMemo(() => {
    if (!data) return { taskCards: [] as TaskCard[], unstarted: [] as UnstartedCard[] };
    const openTasks = data.pickTasks.filter((t) => ["pending", "in_progress"].includes(t.status));
    const soById: Record<string, PickOrder> = {};
    data.pickOrders.forEach((o) => (soById[o.id] = o));

    const matchesTerm = (order: PickOrder, haystack: string[]) => {
      if (!term) return true;
      if (order.order_number.toLowerCase().includes(term)) return true;
      if ((order.customers?.name ?? "").toLowerCase().includes(term)) return true;
      return haystack.some((s) => s.toLowerCase().includes(term));
    };

    const taskCards: TaskCard[] = openTasks
      .map((t) => {
        const order = soById[t.sales_order_id];
        const items = data.pickItems
          .filter((pi) => pi.pick_task_id === t.id && pi.qty_required > pi.qty_picked)
          .map((pi) => ({ ...pi, remaining: pi.qty_required - pi.qty_picked }));
        return { task: t, order, items };
      })
      .filter((c): c is TaskCard => !!c.order && c.items.length > 0)
      .filter((c) =>
        matchesTerm(
          c.order,
          c.items.map((i) => `${historicalProductName(i)} ${historicalSku(i) ?? ""}`),
        ),
      )
      .filter((c) => {
        if (pickFilter === "all") return true;
        if (pickFilter === "mine") return c.task.assigned_to === user?.id;
        if (pickFilter === "unassigned") return !c.task.assigned_to;
        return true;
      });

    // Orders with lines still to pick but no open task yet.
    const taskSoIds = new Set(openTasks.map((t) => t.sales_order_id));
    const unstarted: UnstartedCard[] = data.pickOrders
      .filter((o) => !taskSoIds.has(o.id))
      .map((o) => ({
        order: o,
        slices: pickSlicesFromReservations({
          lines: data.pickLines.filter((l) => l.sales_order_id === o.id && l.qty_allocated > 0),
          ledger: data.allocationLedger,
        }),
      }))
      .filter((g) => g.slices.length > 0)
      .filter((g) =>
        matchesTerm(
          g.order,
          g.slices.map((s) =>
            [
              historicalProductName(s.line),
              historicalSku(s.line) ?? "",
            ].join(" "),
          ),
        ),
      )
      .filter((g) => {
        if (pickFilter === "all" || pickFilter === "unassigned") return true;
        if (pickFilter === "mine") return false;
        return true;
      })
      .sort((a, b) => {
        const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
        const ra = PRIORITY_RANK[a.order.priority ?? "normal"] ?? 2;
        const rb = PRIORITY_RANK[b.order.priority ?? "normal"] ?? 2;
        if (ra !== rb) return ra - rb;
        return String(a.order.order_number).localeCompare(String(b.order.order_number));
      });

    return { taskCards, unstarted };
  }, [data, term, pickFilter, user?.id]);

  // Packed qty per fulfilment_item across all packages.
  const packedByFi = useMemo(() => {
    const map: Record<string, number> = {};
    (data?.packages ?? []).forEach((p) => {
      (p.items ?? []).forEach((it) => {
        if (it.fulfilment_item_id) map[it.fulfilment_item_id] = (map[it.fulfilment_item_id] ?? 0) + (it.qty ?? 0);
      });
    });
    return map;
  }, [data?.packages]);

  const packing = useMemo(() => {
    if (!data) return [];
    const pkgShipmentIds = new Set((data.packages ?? []).map((p) => p.shipment_id));
    return data.fulfilments
      .map((f) => {
        const items = data.fulfilmentItems
          .filter((i) => i.fulfilment_id === f.id)
          .map((i) => ({ ...i, packed: packedByFi[i.id] ?? 0 }));
        const shipmentsFor = data.shipments.filter((s) => s.fulfilment_id === f.id && s.status !== "cancelled");
        if (shipmentsFor.some((s) => shipmentLifecycleComplete(s))) return null;
        const hasPackages = shipmentsFor.some((s) => pkgShipmentIds.has(s.id));
        const total = items.reduce((s, i) => s + i.qty_fulfilled, 0);
        const packedTotal = items.reduce((s, i) => s + Math.min(i.packed, i.qty_fulfilled), 0);
        const allPacked = total > 0 && packedTotal >= total;
        const needsPacking = shipmentsFor.length === 0 || !hasPackages || !allPacked;
        if (!needsPacking) return null;
        return { fulfilment: f, items, shipments: shipmentsFor, allPacked, packedTotal, total };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .filter((g) => {
        if (!term) return true;
        return (
          g.fulfilment.fulfilment_number.toLowerCase().includes(term) ||
          g.fulfilment.sales_orders?.order_number?.toLowerCase().includes(term) ||
          g.items.some((i) => historicalProductName(i).toLowerCase().includes(term))
        );
      });
  }, [data, term, packedByFi]);

  const shipping = useMemo(() => {
    if (!data) return [];
    return data.shipments
      .filter((s) => {
        const cov = shipmentPackageCoverage(
          s.id,
          s.fulfilment_id,
          data.fulfilmentItems,
          data.packages ?? [],
        );
          return shipmentAppearsInShippingQueue({
            status: s.status,
            packed: cov.packed,
            required: cov.required,
            providerSyncStatus: s.provider_sync_status,
          });
      })
      .filter((s) => {
        if (!term) return true;
        return (
          (s.tracking_number ?? "").toLowerCase().includes(term) ||
          s.fulfilments?.sales_orders?.order_number?.toLowerCase().includes(term) ||
          s.fulfilments?.sales_orders?.customers?.name?.toLowerCase().includes(term)
        );
      });
  }, [data, term]);

  const counts = { picking: picking.taskCards.length + picking.unstarted.length, packing: packing.length, dispatch: shipping.length };
  const canPrint = canPrintOrderDocuments(role);
  const visiblePrintIds = useMemo(
    () =>
      visibleFulfilmentPrintOrderIds({
        tab,
        pickingOrderIds: [...picking.taskCards.map((c) => c.order.id), ...picking.unstarted.map((c) => c.order.id)],
        packingOrderIds: packing.map((p) => p.fulfilment.sales_order_id),
        dispatchOrderIds: shipping.map((s) => s.fulfilments?.sales_orders?.id),
      }),
    [tab, picking, packing, shipping],
  );
  const printRows = useMemo(() => visiblePrintIds.map((id) => ({ id })), [visiblePrintIds]);
  const bulk = useBulkSelection(printRows);

  const printSelected = async (kind: "pick" | "pack" | "invoice") => {
    if (!org?.id || bulk.selectedIds.length === 0) return;
    setError("");
    try {
      await printSalesOrderDocuments(org.id, bulk.selectedIds, kind);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const tabClass = (t: FulfilmentTab) =>
    `px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
      tab === t ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
    }`;

  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Fulfilment</h1>
          <p className="text-sm text-slate-500 mt-1">Picking, packing, dispatch and shipments — one workspace</p>
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search picking, packing and shipping"
            placeholder="Scan or search SKU, order, customer..."
            className="w-64 pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
        </div>
        <button
          onClick={() => navigate(fulfilmentLookupPath())}
          className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-scan-line" /> Barcode lookup
        </button>
      </div>

      {tab === "picking" && (
        <div className="mb-3 flex items-center gap-2">
          <div className="inline-flex items-center bg-slate-100 rounded-full p-1">
            {(["all", "mine", "unassigned"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setPickFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  pickFilter === f ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {f === "all" ? "All picks" : f === "mine" ? "My picks" : "Unassigned"}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="inline-flex items-center bg-slate-100 rounded-full p-1 mb-5" role="tablist" aria-label="Fulfilment stages">
        <button onClick={() => setTab("picking")} role="tab" aria-selected={tab === "picking"} className={tabClass("picking")}>
          <span className="inline-flex items-center gap-2">
            Picking
            <span className="text-xs bg-white/20 text-inherit px-1.5 py-0.5 rounded-full">{counts.picking}</span>
          </span>
        </button>
        <button onClick={() => setTab("packing")} role="tab" aria-selected={tab === "packing"} className={tabClass("packing")}>
          <span className="inline-flex items-center gap-2">
            Packing
            <span className="text-xs bg-white/20 text-inherit px-1.5 py-0.5 rounded-full">{counts.packing}</span>
          </span>
        </button>
        <button onClick={() => setTab("dispatch")} role="tab" aria-selected={tab === "dispatch"} className={tabClass("dispatch")}>
          <span className="inline-flex items-center gap-2">
            Dispatch
            <span className="text-xs bg-white/20 text-inherit px-1.5 py-0.5 rounded-full">{counts.dispatch}</span>
          </span>
        </button>
        <button onClick={() => setTab("shipments")} role="tab" aria-selected={tab === "shipments"} className={tabClass("shipments")}>
          Shipments
        </button>
      </div>

      {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {tab !== "shipments" && visiblePrintIds.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={bulk.allSelected}
            ref={(el) => {
              if (el) el.indeterminate = bulk.someSelected && !bulk.allSelected;
            }}
            onChange={bulk.toggleAll}
            aria-label="Select all visible orders"
            className="w-4 h-4 rounded accent-emerald-600"
          />
          <span className="text-xs text-slate-500">Select all visible</span>
        </div>
      )}
      {canPrint && bulk.selectedIds.length > 0 && (
        <BulkActionBar count={bulk.selectedIds.length} onClear={bulk.clear}>
          <PrintDocumentsMenu mode="bulk" onPrint={(kind) => void printSelected(kind)} />
        </BulkActionBar>
      )}

      {tab === "shipments" ? (
        <Shipments embedded />
      ) : isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load warehouse data.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      ) : tab === "picking" ? (
        picking.taskCards.length === 0 && picking.unstarted.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200">
            <EmptyState icon="ri-scan-2-line" title="Nothing to pick" description="Orders with stock ready to fulfil will appear here, grouped by location." />
          </div>
        ) : (
          <div className="space-y-4">
            {picking.taskCards.map(({ task, order, items }) => (
              <div key={task.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="checkbox"
                      checked={bulk.selected.has(order.id)}
                      onChange={() => bulk.toggle(order.id)}
                      aria-label={`Select ${order.order_number}`}
                      className="w-4 h-4 rounded accent-emerald-600"
                    />
                    <span className="text-sm font-semibold text-slate-800 font-mono">{order.order_number}</span>
                    <span className="text-xs text-slate-400">{order.customers?.name ?? "—"}</span>
                    <StatusBadge status={pickTaskBadgeStatus(task.status)} customLabel={pickTaskLabel(task.status)} />
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <i className="ri-map-pin-line" aria-hidden="true" /> Pick from {task.locations?.name ?? "—"}
                    </span>
                    <span className="text-xs text-slate-500">Deliver to {formatShipTo(order.shipping_address)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {task.assigned_to ? (
                      <span className="text-xs text-slate-500">
                        {task.assigned_to === user?.id ? "Assigned to you" : "Assigned to another worker"}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Unassigned</span>
                    )}
                    {canPick && (
                      <button
                        onClick={() => navigate(fulfilmentPickPath(task.id))}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-play-circle-line" aria-hidden="true" /> Resume picking
                      </button>
                    )}
                    <button
                      onClick={() => void printOrder(order.id, "pick")}
                      className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                    >
                      Print pick slip
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-100">
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">To pick</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Picked</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((pi) => (
                        <tr key={pi.id} className="border-b border-slate-50 last:border-0">
                          <td className="px-4 py-2.5 text-sm text-slate-800">
                            {historicalProductName(pi)}
                            {(variantLabel(historicalVariantOptions(pi)) || historicalSku(pi)) && (
                              <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(pi)) || historicalSku(pi)}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-slate-500 font-mono">{canonicalLineSku({ variantSku: pi.variants?.sku, productSku: pi.products?.sku }) ?? "—"}</td>
                          <td className="px-4 py-2.5 text-sm font-semibold text-slate-900 text-right">{pi.qty_required}</td>
                          <td className={`px-4 py-2.5 text-sm text-right font-medium ${pi.qty_picked > 0 ? "text-emerald-700" : "text-slate-400"}`}>{pi.qty_picked}</td>
                          <td className="px-4 py-2.5 text-sm text-amber-600 text-right font-medium">{pi.remaining}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {picking.unstarted.map(({ order, slices }) => (
              <div key={order.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="checkbox"
                      checked={bulk.selected.has(order.id)}
                      onChange={() => bulk.toggle(order.id)}
                      aria-label={`Select ${order.order_number}`}
                      className="w-4 h-4 rounded accent-emerald-600"
                    />
                    <span className="text-sm font-semibold text-slate-800 font-mono">{order.order_number}</span>
                    <span className="text-xs text-slate-400">{order.customers?.name ?? "—"}</span>
                    <StatusBadge status="info" customLabel="To pick" />
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <i className="ri-map-pin-line" aria-hidden="true" /> {pickLocationHeader(slices.map((s) => s.locationName))}
                    </span>
                    <span className="text-xs text-slate-500">Deliver to {formatShipTo(order.shipping_address)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {canPick && (
                      <button
                        onClick={() => startPick(order.id)}
                        disabled={busyId === order.id}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-checkbox-circle-line" aria-hidden="true" /> Start picking
                      </button>
                    )}
                    <button
                      onClick={() => void printOrder(order.id, "pick")}
                      className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                    >
                      Print pick slip
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-100">
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">SKU</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">To pick</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slices.map((slice, idx) => {
                        const l = slice.line;
                        const sku = historicalSku(l);
                        return (
                        <tr key={`${l.id}:${slice.locationId ?? "none"}:${idx}`} className="border-b border-slate-50 last:border-0">
                          <td className="px-4 py-2.5 text-sm text-slate-800">
                            {historicalProductName(l)}
                            {historicalVariantOptions(l) && (variantLabel(historicalVariantOptions(l)) || historicalSku(l)) && (
                              <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(l)) || historicalSku(l)}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-sm text-slate-500 font-mono">{sku ?? "—"}</td>
                          <td className="px-4 py-2.5 text-sm text-slate-600">{slice.locationName ?? "—"}</td>
                          <td className="px-4 py-2.5 text-sm font-semibold text-slate-900 text-right">{slice.remaining}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )
      ) : tab === "packing" ? (
        packing.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200">
            <EmptyState icon="ri-inbox-2-line" title="Nothing to pack" description="Fulfilled orders awaiting parcel assembly will appear here." />
          </div>
        ) : (
          <div className="space-y-4">
            {packing.map(({ fulfilment, items, shipments, allPacked, packedTotal, total }) => (
              <div key={fulfilment.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    {fulfilment.sales_order_id && (
                      <input
                        type="checkbox"
                        checked={bulk.selected.has(fulfilment.sales_order_id)}
                        onChange={() => bulk.toggle(fulfilment.sales_order_id!)}
                        aria-label={`Select ${fulfilment.sales_orders?.order_number ?? fulfilment.fulfilment_number}`}
                        className="w-4 h-4 rounded accent-emerald-600"
                      />
                    )}
                    <span className="text-sm font-semibold text-slate-800 font-mono">{fulfilment.fulfilment_number}</span>
                    <span className="text-xs text-slate-400 font-mono">{fulfilment.sales_orders?.order_number ?? "—"}</span>
                    <span className="text-xs text-slate-400">{fulfilment.sales_orders?.customers?.name ?? "—"}</span>
                    <span className="text-xs text-slate-500">Deliver to {formatShipTo(fulfilment.sales_orders?.shipping_address)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">
                      Boxed <span className={`font-semibold ${allPacked ? "text-emerald-700" : "text-slate-800"}`}>{packedTotal}</span> / {total}
                    </span>
                    <div className="flex items-center gap-2">
                      {fulfilment.sales_order_id && (
                        <button
                          onClick={() => void printOrder(fulfilment.sales_order_id!, "pack")}
                          className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                        >
                          Print packing slip
                        </button>
                      )}
                      <button
                        onClick={() => navigate(fulfilmentPackingPath(fulfilment.id))}
                        className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                        title="Barcode-verified packing"
                      >
                        <i className="ri-scan-line" /> Verify items
                      </button>
                      {shipments.length === 0 ? (
                        canPick && (
                          <button
                            onClick={() => createShipment(fulfilment.id)}
                            disabled={busyId === fulfilment.id}
                            className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                          >
                            <i className="ri-add-line" /> {busyId === fulfilment.id ? "Creating..." : "Create shipment"}
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => navigate(fulfilmentShipmentPath(shipments[0].id))}
                          className={`px-3 py-1.5 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1 ${
                            allPacked ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"
                          }`}
                        >
                          <i className="ri-box-3-line" /> {allPacked ? "Ready to ship" : "Pack parcels"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-slate-50">
                  {items.map((it) => (
                    <div key={it.id} className="px-4 py-2.5 flex items-center gap-4 text-sm">
                      <span className="flex-1 text-slate-800">
                        {historicalProductName(it)}
                        {it.products?.sku && <span className="text-xs text-slate-400 font-mono ml-2">{it.products.sku}</span>}
                      </span>
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        {it.products?.weight_kg != null ? `${it.products.weight_kg} kg` : "—"}
                      </span>
                      <span className={`text-sm font-semibold whitespace-nowrap ${it.packed >= it.qty_fulfilled ? "text-emerald-700" : "text-slate-600"}`}>
                        boxed {Math.min(it.packed, it.qty_fulfilled)}/{it.qty_fulfilled}
                      </span>
                    </div>
                  ))}
                  {items.length === 0 && <div className="px-4 py-5 text-center text-sm text-slate-400">No items recorded</div>}
                </div>
              </div>
            ))}
          </div>
        )
      ) : shipping.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState icon="ri-truck-line" title="Nothing to dispatch" description="Packed orders awaiting dispatch will appear here." />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={bulk.allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = bulk.someSelected && !bulk.allSelected;
                      }}
                      onChange={bulk.toggleAll}
                      aria-label="Select all visible orders"
                      className="w-4 h-4 rounded accent-emerald-600"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Deliver to</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tracking</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shipping.map((s) => {
                  const label = viewPrintLabelAction(s);
                  const trackUrl = isAustraliaPostShipment(s) ? australiaPostTrackingUrl(s.tracking_number) : null;
                  return (
                  <tr key={s.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      {s.fulfilments?.sales_orders?.id && (
                        <input
                          type="checkbox"
                          checked={bulk.selected.has(s.fulfilments.sales_orders.id)}
                          onChange={() => bulk.toggle(s.fulfilments!.sales_orders!.id!)}
                          aria-label={`Select ${s.fulfilments?.sales_orders?.order_number ?? "order"}`}
                          className="w-4 h-4 rounded accent-emerald-600"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800 font-mono">{s.fulfilments?.sales_orders?.order_number ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{s.fulfilments?.sales_orders?.customers?.name ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatShipTo(s.fulfilments?.sales_orders?.shipping_address)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{shipmentCarrierLabel(s)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                      <div className="flex flex-col gap-1">
                        <span>{s.tracking_number ?? "—"}</span>
                        {trackUrl && (
                          <a href={trackUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-emerald-600 hover:text-emerald-700">
                            Track on Australia Post
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={shipmentOperatorStatus(s).badge} customLabel={shipmentOperatorStatus(s).label} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                        {canShip && shipmentCanDispatchFromQueue(s.status) && !shipmentLifecycleComplete(s) && (
                          <button
                            onClick={() => dispatch(s.id)}
                            disabled={busyId === s.id}
                            className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                          >
                            {busyId === s.id ? "Dispatching..." : "Dispatch"}
                          </button>
                        )}
                        {label.kind === "open-url" && (
                          <a
                            href={label.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                          >
                            View &amp; print label
                          </a>
                        )}
                        <button
                          onClick={() => navigate(fulfilmentShipmentPath(s.id))}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}